import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { StackDivider, VStack } from '@chakra-ui/react';
import { FaRegAddressCard } from 'react-icons/fa';
import { FiEdit, FiFileText, FiMessageSquare, FiPlusSquare, FiUser, FiUserCheck, FiUsers } from 'react-icons/fi';
import { AiOutlineApi } from 'react-icons/ai';
import { MdOutlineIntegrationInstructions } from 'react-icons/md';
import { Loader } from '@netiva/classifieds-ui';

import { MessagesEnabled } from '@/environment';
import { routes } from '@/lib/routes';
import { useAppSelector } from '@/store';
import { CommonSteps } from '../Ad/constants';
import { HomeCard, HomeCardContainer, HomeSection } from './components';

export const HomePage: FC = memo(function HomePage() {
  const { t } = useTranslation();
  const {
    isLoaded: isAccountLoaded,
    hasMemberContract,
    hasValidAccount,
    invitations,
    allowCreateObjects,
    unreadMessageCount,
  } = useAppSelector((state) => state.account);

  if (isAccountLoaded && !hasValidAccount) {
    return <Navigate to={routes.newAccount()} />;
  }

  return (
    <>
      <Loader isLoading={!isAccountLoaded} showOverlay />
      <VStack align="stretch" divider={<StackDivider />} spacing={8}>
        <HomeSection title={t('ui.navigation.section.ads')}>
          {hasValidAccount && (
            <HomeCardContainer>
              <HomeCard header={t('ui.navigation.myads')} link={routes.myAds()} icon={FiEdit} />
              {allowCreateObjects && (
                <HomeCard
                  header={t('ui.navigation.newad')}
                  link={routes.ad(0, CommonSteps[0].key)}
                  icon={FiPlusSquare}
                />
              )}
              {MessagesEnabled && (
                <HomeCard
                  header={t('ui.navigation.messages')}
                  link={routes.messages()}
                  icon={FiMessageSquare}
                  badgeCount={unreadMessageCount}
                />
              )}
            </HomeCardContainer>
          )}
        </HomeSection>

        <HomeSection title={t('ui.navigation.section.account')}>
          <HomeCardContainer>
            <HomeCard header={t('ui.navigation.account')} link={routes.account()} icon={FiUser} />
            {hasValidAccount && (
              <>
                <HomeCard header={t('ui.navigation.addresses')} link={routes.addresses()} icon={FaRegAddressCard} />
                {hasMemberContract && (
                  <HomeCard header={t('ui.navigation.products')} link={routes.products()} icon={FiFileText} />
                )}
                <HomeCard header={t('ui.navigation.logins')} link={routes.logins()} icon={FiUsers} />
              </>
            )}
            {invitations.length > 0 && (
              <HomeCard
                header={t('ui.navigation.invitations')}
                link={routes.invitations()}
                icon={FiUserCheck}
                badgeCount={invitations.length}
              />
            )}
          </HomeCardContainer>
        </HomeSection>

        {hasMemberContract && (
          <HomeSection title={t('ui.navigation.section.sdk')}>
            <HomeCardContainer>
              <HomeCard header={t('ui.navigation.sdk')} link={routes.sdk()} icon={MdOutlineIntegrationInstructions} />
              <HomeCard header={t('ui.navigation.api')} link={routes.api()} icon={AiOutlineApi} />
            </HomeCardContainer>
          </HomeSection>
        )}
      </VStack>
    </>
  );
});
